/**
 * @fileoverview Custom hook to track box impression events using IntersectionObserver.
 * Automatically tracks when elements become visible in the viewport.
 */

import { useEffect, useRef } from "react";
import { useStandardizedTracking, EventNames } from "./useStandardizedTracking";

/**
 * Options for box impression tracking.
 *
 * @typedef {Object} UseBoxImpressionTrackingOptions
 * @property {string} featureGroup - Feature group from FeatureGroups.
 * @property {string} [feature] - Specific feature identifier (e.g., "ee_first_step", "smart_sign", "docusign").
 * @property {string} [pageKey] - Logical page name.
 * @property {string} contentType - Content type from ContentTypes.
 * @property {string} contentKey - Content identifier.
 * @property {string} layout - Layout type from LayoutTypes.
 * @property {Object} [potentialSaving] - Potential saving object for AP fields.
 * @property {Object} [assistanceProgram] - Assistance program object for AP fields.
 * @property {Object} [additionalInfo] - Additional event-specific data.
 * @property {boolean} [enabled=true] - Whether tracking is enabled.
 * @property {number} [threshold=0.5] - Intersection threshold (0-1, defaults to 0.5 meaning 50% visible).
 * @property {number} [visibilityDuration=0] - Duration in milliseconds that the element must remain visible before tracking (defaults to 0, meaning track immediately).
 */

/**
 * Custom hook to track box impression events using IntersectionObserver.
 * Automatically handles visibility detection and one-time tracking.
 *
 * @param {UseBoxImpressionTrackingOptions} options - Tracking options.
 * @returns {React.RefObject<HTMLElement | null>} Ref to attach to the element to track.
 *
 * @example
 * ```jsx
 * // Basic usage - fires immediately when 50% visible
 * const cardRef = useBoxImpressionTracking({
 *   featureGroup: FeatureGroups.POTENTIAL_SAVINGS,
 *   pageKey: "patient_potential_savings",
 *   contentType: ContentTypes.APPLICATION,
 *   contentKey: "assistance_program_card",
 *   layout: LayoutTypes.CARD,
 *   potentialSaving,
 *   assistanceProgram,
 *   additionalInfo: {
 *     position_in_list: positionInList,
 *     is_express_enroll: !!isExpressEnrollAP
 *   }
 * });
 *
 * return <div ref={cardRef}>Card content</div>;
 * ```
 *
 * @example
 * ```jsx
 * // With visibility duration - fires after 1s of continuous visibility
 * const panelRef = useBoxImpressionTracking({
 *   featureGroup: FeatureGroups.POTENTIAL_SAVINGS,
 *   contentType: ContentTypes.APPLICATION,
 *   contentKey: "program_details_panel",
 *   layout: LayoutTypes.PANEL,
 *   visibilityDuration: 1000
 * });
 * ```
 */
export const useBoxImpressionTracking = ({
  featureGroup,
  feature,
  pageKey,
  contentType,
  contentKey,
  layout,
  potentialSaving,
  assistanceProgram,
  additionalInfo = {},
  enabled = true,
  threshold = 0.5,
  visibilityDuration = 0
}) => {
  /** @type {React.RefObject<HTMLElement | null>} */
  const elementRef = useRef(null);
  const impressionTracked = useRef(false);
  const visibilityTimerRef = useRef(null);
  const { track } = useStandardizedTracking();

  useEffect(() => {
    if (!elementRef.current || impressionTracked.current || !enabled) {
      return;
    }

    const fireImpression = () => {
      if (impressionTracked.current) return;

      const event = {
        featureGroup,
        feature,
        pageKey,
        contentType,
        contentKey,
        layout,
        potentialSaving,
        assistanceProgram,
        additionalInfo
      };

      track(EventNames.BOX_IMPRESSION, event);

      impressionTracked.current = true;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isVisible = entry.isIntersecting && entry.intersectionRatio >= threshold;

          if (isVisible && !impressionTracked.current) {
            if (visibilityDuration > 0) {
              // Start timer if not already running
              if (!visibilityTimerRef.current) {
                visibilityTimerRef.current = setTimeout(() => {
                  fireImpression();
                }, visibilityDuration);
              }
            } else {
              // Fire immediately (original behavior)
              fireImpression();
            }
          } else if (!isVisible && visibilityTimerRef.current) {
            // Reset timer if visibility drops below threshold
            clearTimeout(visibilityTimerRef.current);
            visibilityTimerRef.current = null;
          }
        });
      },
      { threshold }
    );

    observer.observe(elementRef.current);

    return () => {
      observer.disconnect();
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
        visibilityTimerRef.current = null;
      }
    };
  }, [
    featureGroup,
    feature,
    pageKey,
    contentType,
    contentKey,
    layout,
    potentialSaving,
    assistanceProgram,
    additionalInfo,
    enabled,
    threshold,
    visibilityDuration,
    track
  ]);

  return elementRef;
};
